import React, { FC, ReactNode, useState, useEffect } from "react";
import classNames from "classnames";
import { View } from "@tarojs/components";
import useGenerateId from "@/hooks/useGenerateId";
import { pxTransform } from "@/utils";
import Group from "./Group";
import "./index.scss";
import type { XHComponentCommonProps } from "../../types";
import Icon from "../Icon";

type CompoundedComponent = React.ForwardRefExoticComponent<
  XHCheckboxProps & React.RefAttributes<HTMLInputElement>
> & {
  Group: typeof Group;
};

export type XHCheckboxProps = {
  onChange?: (check: boolean, value: string) => void;
  checked?: boolean;
  name?: string;
  label: string | ReactNode;
  size?: number;
  circle?: boolean;
  align?: "center" | "end";
  direction?: "rtl";
  justify?: "around" | "between";
  value?: string | number;
  id?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  /**
   * 抖音不支持属性选择器
   * @description 主要为抖音支持颜色
   */
  color?: string;
  /**
   * 选中状态下打勾icon的颜色
   */
  iconColor?: string;
  dataTrackId?: string;
} & XHComponentCommonProps;

const XHCheckbox: FC<XHCheckboxProps> = props => {
  const {
    className,
    name,
    label,
    id,
    circle,
    onChange,
    align,
    direction,
    justify,
    checked = false,
    disabled,
    value = "",
    style,
    size = 0,
    iconColor = "#FFFFFF",
    dataTrackId = "Checkbox",
  } = props;

  const finalId = useGenerateId(id, "xh-checkbox-id");
  const [innerChecked, setInnerChecked] = useState(false);

  useEffect(() => {
    setInnerChecked(checked);
  }, [checked]);

  const handleOnchange = e => {
    let finalchecked = checked || false;
    let value = "";
    if (process.env.TARO_ENV === "h5") {
      finalchecked = e.target.checked;
      value = e.target.value;
    } else {
      finalchecked = e.detail.value.length > 0;
      value = e.detail.value[0];
    }
    onChange && onChange(finalchecked, value);
  };

  const handleFakeChange = () => {
    if (disabled) return;
    setInnerChecked(!innerChecked);
    onChange?.(!innerChecked, value as string);
  };

  const cls = classNames("xh-checkbox", className, align, direction, justify, {
    h5: process.env.TARO_ENV === "h5",
  });
  if (process.env.TARO_ENV !== "h5") {
    return (
      <View
        onClick={handleFakeChange}
        className={cls}
        style={style}
        // @ts-ignore
        dataTrackId={dataTrackId}
      >
        <View
          className={classNames("xh-checkbox-input", {
            checked: innerChecked,
            disabled,
          })}
        >
          {" "}
        </View>
        <span
          style={
            size > 0
              ? {
                  width: pxTransform(size),
                  height: pxTransform(size),
                }
              : {}
          }
          // @ts-ignore
          dataTrackId={`${dataTrackId}-fake`}
          className={classNames("xh-checkbox-input-fake", circle && "circle")}
        >
          <Icon
            icon="duigou"
            className="xh-checkbox-input-fake-icon"
            color={iconColor}
            size={size ? size * 0.55 : 18}
          />
        </span>
        <span
          className="xh-radio-label"
          // @ts-ignore
          dataTrackId={`${dataTrackId}-label`}
        >
          {label}
        </span>
      </View>
    );
  }

  return (
    <label
      htmlFor={finalId}
      className={cls}
      style={style}
      data-track-id={dataTrackId}
    >
      <input
        type="checkbox"
        id={finalId}
        name={name}
        className="xh-checkbox-input"
        onChange={handleOnchange}
        value={value}
        defaultChecked={checked}
      />
      <span
        style={
          size > 0
            ? {
                width: pxTransform(size),
                height: pxTransform(size),
              }
            : {}
        }
        data-track-id={`${dataTrackId}-fake`}
        className={classNames("xh-checkbox-input-fake", circle && "circle")}
      >
        <Icon
          icon="duigou"
          className="xh-checkbox-input-fake-icon"
          color={iconColor}
          size={size ? size * 0.55 : 18}
        />
      </span>
      <span
        className="xh-checkbox-label"
        data-track-id={`${dataTrackId}-label`}
      >
        {label}
      </span>
    </label>
  );
};

const CheckBox = XHCheckbox as CompoundedComponent;
CheckBox.Group = Group;

export default CheckBox;
